import React, { useEffect, useMemo, useState } from 'react'

import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'

import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'

import styles from './../Dialog.module.sass'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useSession from 'hooks/useSession'
import HFDropdown from 'components/HFInputs/HFDropdown'

import { ticketRequestSchema } from './TicketRequestDialogSchema'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { useFetchList } from 'store'
import { Options } from 'models'
import HFMultiSelect from 'components/HFInputs/HFMultipleSelect'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'

const TicketRequestDialog = (props) => {
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const { className, data, dismiss, resolve } = props
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const getSimpleTickets =
		(data.ticketId || data.appointmentId || data.requisitionId) !==
		undefined

	const {
		contents: [
			ticketTypesList,
			providerEntities,
			insuranceCompanies,
			medicalActs,
		],
	} = useFetchList(
		[
			getSimpleTickets
				? 'metadata/simpleTicketTypes'
				: 'metadata/ticketTypes',
			'metadata/provider_entities',
			'metadata/insurance_companies',
			'metadata/medical_acts',
		],
		[
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
		]
	)

	const ticketTypes = useMemo(() => {
		if (data.branch !== 'AT') return ticketTypesList

		return (ticketTypesList || []).filter((item) => item.value !== '1')
	}, [data?.branch, ticketTypesList])

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(ticketRequestSchema),
		shouldUnregister: true,
	})
	const {
		handleSubmit,
		formState,
		setValue,
		watch,
		control,
		getValues,
		register,
	} = methods

	const selectedType = watch('type')
	const showExtraFields = selectedType === '9'

	useEffect(() => {
		setValue('type', ticketTypes?.[0]?.value)
		// setValue('provider', '')
		// setValue('insuranceCompanies', [])
		// setValue('medicalAct', [])
		// eslint-disable-next-line
	}, [setValue, ticketTypes?.length])

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const endpoint = `${API_URL}/api/clinical_processes/${data?.processId}/ticket_request`

		setError(null)
		setSubmitting('submitting')

		console.log('🚀 ~ TicketRequestDialog ~ formData:', formData)

		setSubmitting(false)
		return fetch(
			endpoint,
			fetchOptions({
				method: 'POST',
				body: {
					...formData,
					ticketId: data.ticketId ?? null,
					appointmentId: data.appointmentId ?? null,
					requisitionId: data.requisitionId ?? null,
				},
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				pushNotification(
					undefined,
					'success',
					i18n`Ticket requested successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
			console.log('form ', getValues())
			console.log(
				'🚀 ~ file: AdministrativeOpinion.js ~ line 96 ~ useEffect ~ errorMessages',
				errorMessages
			)
		}

		// console.log(error)
		// console.log({ data })
	}, [formState.errors, i18n])

	const medicalActsWatch = useWatch({
		control,
		name: 'medicalAct',
		defaultValue: [],
	})

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_administrative_opinion'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['root'] || ''} ${className || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<HFDropdown
							placeholder={i18n`Ticket type`}
							name='type'
							options={ticketTypes ?? []}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
						/>
					</fieldset>

					{/* {showExtraFields && (
						<>
							<fieldset>
								<HFDropdown
									placeholder={i18n`Provider`}
									name='provider'
									options={providerEntities ?? []}
									searchable={true}
									optionsAlign='right'
									noData={i18n`No results`}
									required
								/>
								<HFMultiSelect
									className={styles['value-selector']}
									placeholder={i18n`insuranceCompany`}
									name='insuranceCompanies'
									options={insuranceCompanies}
									searchable={true}
									optionsAlign='right'
									noData={i18n`No results`}
									required
								/>
								<HFMultiSelect
									className={styles['value-selector']}
									placeholder={i18n`Medical Act`}
									name='medicalAct'
									options={medicalActs ?? []}
									searchable={true}
									optionsAlign='right'
									noData={i18n`No results`}
									required
								/>
							</fieldset>
							{(medicalActsWatch || []).map((act, index) => {
								const medicalAct = medicalActs?.find(
									(ma) => ma.value === act
								)
								return (
									<fieldset key={act?.value ?? index}>
										<VStack gap='xsmall'>
											<label>
												{medicalAct?.text ??
													`Medical Act ${index + 1}`}
											</label>

											<input
												type='hidden'
												{...register(
													`medicalActPrices.${index}.medicalActId`
												)}
												value={act}
												readOnly
											/>
											<HFTextInput
												placeholder={i18n`Price`}
												name={`medicalActPrices.${index}.price`}
												type='number'
												required
											/>
										</VStack>
									</fieldset>
								)
							})}
						</>
					)} */}
					<fieldset>
						<HFTextInput
							placeholder={i18n`Description`}
							name='description'
							multiline
							noResize
							rows='3'
							required
						/>
					</fieldset>
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_administrative_opinion`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default TicketRequestDialog
